'use client';

import { c as _c } from "react/compiler-runtime";
import { MapPin, MapPinSolid } from '@byway/icons';
import { useTranslations } from 'next-intl';
import { DialogTrigger } from 'react-aria-components';
import { Button } from '~/components/Button';
import { Popover } from '~/components/Popover';
import { useToasts } from '~/components/Toast';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { usePathname, useRouter } from '~/i18n/navigation';
import { usePlaygroundActions, usePlaygroundContextParams, usePlaygroundLastAddedDestination, usePlaygroundPreferences, usePlaygroundSelectedDestinations, usePlaygroundUIState } from '~/stores/playgroundStore';
import DestinationsPanel from '../Drawers/DestinationsPanel';
import styles from './DestinationsButton.module.css';
import { formatDestsToPlaceNames, PRIMARY_EDIT_BAR_POPOVERS_MAX_WIDTH, PRIMARY_EDIT_BAR_POPOVERS_OFFSET } from './helpers';
interface DestinationsButtonProps {
  buttonVariant: 'pill' | 'tertiary';
}
interface DestinationsButtonMobileProps extends DestinationsButtonProps {
  maxLength?: number;
}

/**
 * Desktop destinations button with popover
 */
export function DestinationsButton(t0) {
  const $ = _c(33);
  const {
    buttonVariant
  } = t0;
  const pathname = usePathname();
  const isDesktop = useIsDesktop();
  const {
    isDrawerOpen,
    drawerItem
  } = usePlaygroundUIState();
  const {
    closeDrawer,
    openDrawer,
    removeUnsupportedDestination
  } = usePlaygroundActions();
  const {
    dpPlaces: selectedPlaces
  } = usePlaygroundSelectedDestinations();
  const {
    unsupportedDestinations
  } = usePlaygroundPreferences();
  const lastAddedDestination = usePlaygroundLastAddedDestination();
  const preferences = usePlaygroundContextParams();
  const router = useRouter();
  const toasts = useToasts();
  const t = useTranslations();
  let t1;
  if ($[0] !== pathname) {
    t1 = pathname?.includes("/review");
    $[0] = pathname;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const isReview = t1;
  const hasStartDate = typeof preferences?.startDate !== "undefined" && preferences?.startDate !== null;
  const isPopoverOpen = Boolean(isDesktop && isDrawerOpen && drawerItem === "destinations");
  let t2;
  if ($[2] !== hasStartDate || $[3] !== isReview || $[4] !== openDrawer || $[5] !== router || $[6] !== t || $[7] !== toasts) {
    t2 = () => {
      if (isReview) {
        router.back();
      }
      if (!hasStartDate) {
        toasts.add({
          title: t("content.feedback.dates_select_title"),
          description: t("content.feedback.dates_select_description"),
          variant: "info",
          position: "bottom"
        }, {
          timeout: 5000
        });
        openDrawer("dates");
        return;
      }
      openDrawer("destinations");
    };
    $[2] = hasStartDate;
    $[3] = isReview;
    $[4] = openDrawer;
    $[5] = router;
    $[6] = t;
    $[7] = toasts;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const handlePress = t2;
  let t3;
  if ($[9] !== closeDrawer || $[10] !== removeUnsupportedDestination || $[11] !== unsupportedDestinations) {
    t3 = isOpen => {
      if (!isOpen) {
        if (unsupportedDestinations.length > 0) {
          unsupportedDestinations.forEach(place => {
            removeUnsupportedDestination(place.id);
          });
        }
        closeDrawer();
      }
    };
    $[9] = closeDrawer;
    $[10] = removeUnsupportedDestination;
    $[11] = unsupportedDestinations;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const handleOpenChange = t3;
  const t4 = selectedPlaces.length > 0;
  let t5;
  if ($[13] !== lastAddedDestination || $[14] !== selectedPlaces || $[15] !== t) {
    t5 = <div className={styles.buttonContent}>{selectedPlaces.length > 0 ? <><MapPinSolid decorative={true} /><span>{formatDestsToPlaceNames(selectedPlaces, lastAddedDestination, 20)}</span></> : <><MapPin decorative={true} /><span>{t("playground.places_add")}</span></>}</div>;
    $[13] = lastAddedDestination;
    $[14] = selectedPlaces;
    $[15] = t;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== buttonVariant || $[18] !== handlePress || $[19] !== t4 || $[20] !== t5) {
    t6 = <Button variant={buttonVariant} size="extraSmall" isPressed={t4} data-ua-click="playground-add-destinations" dataTestId="playground-destinations-button" onPress={handlePress}>{t5}</Button>;
    $[17] = buttonVariant;
    $[18] = handlePress;
    $[19] = t4;
    $[20] = t5;
    $[21] = t6;
  } else {
    t6 = $[21];
  }
  let t7;
  if ($[22] !== t) {
    t7 = t("aria.destinations_edit");
    $[22] = t;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  let t8;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <DestinationsPanel />;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== handleOpenChange || $[26] !== t7) {
    t9 = <Popover ariaLabel={t7} maxWidth={PRIMARY_EDIT_BAR_POPOVERS_MAX_WIDTH} offset={PRIMARY_EDIT_BAR_POPOVERS_OFFSET} placement="bottom" isDismissable={true} noHeader={true} onOpenChange={handleOpenChange} close={true}>{t8}</Popover>;
    $[25] = handleOpenChange;
    $[26] = t7;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== handleOpenChange || $[29] !== isPopoverOpen || $[30] !== t6 || $[31] !== t9) {
    t10 = <DialogTrigger isOpen={isPopoverOpen} onOpenChange={handleOpenChange}>{t6}{t9}</DialogTrigger>;
    $[28] = handleOpenChange;
    $[29] = isPopoverOpen;
    $[30] = t6;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  return t10;
}

/**
 * Mobile destinations button (drawer trigger)
 */
export function DestinationsButtonMobile(t0) {
  const $ = _c(22);
  const {
    buttonVariant,
    maxLength: t1
  } = t0;
  const maxLength = t1 === undefined ? 10 : t1;
  const pathname = usePathname();
  const {
    openDrawer
  } = usePlaygroundActions();
  const {
    dpPlaces: selectedPlaces
  } = usePlaygroundSelectedDestinations();
  const lastAddedDestination = usePlaygroundLastAddedDestination();
  const preferences = usePlaygroundContextParams();
  const router = useRouter();
  const toasts = useToasts();
  const t = useTranslations();
  let t2;
  if ($[0] !== pathname) {
    t2 = pathname?.includes("/review");
    $[0] = pathname;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isReview = t2;
  const hasStartDate = typeof preferences?.startDate !== "undefined" && preferences?.startDate !== null;
  let t3;
  if ($[2] !== hasStartDate || $[3] !== isReview || $[4] !== openDrawer || $[5] !== router || $[6] !== t || $[7] !== toasts) {
    t3 = () => {
      if (isReview) {
        router.back();
        return;
      }
      if (!hasStartDate) {
        toasts.add({
          title: t("content.feedback.dates_select_title"),
          description: t("content.feedback.dates_select_description"),
          variant: "info",
          position: "top"
        }, {
          timeout: 5000
        });
        openDrawer("dates");
        return;
      }
      openDrawer("destinations");
    };
    $[2] = hasStartDate;
    $[3] = isReview;
    $[4] = openDrawer;
    $[5] = router;
    $[6] = t;
    $[7] = toasts;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const handlePress = t3;
  const t4 = selectedPlaces.length > 0;
  let t5;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <MapPinSolid decorative={true} />;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  let t6;
  if ($[10] !== lastAddedDestination || $[11] !== maxLength || $[12] !== selectedPlaces || $[13] !== t) {
    t6 = selectedPlaces.length > 0 ? formatDestsToPlaceNames(selectedPlaces, lastAddedDestination, maxLength) : t("playground.places_add");
    $[10] = lastAddedDestination;
    $[11] = maxLength;
    $[12] = selectedPlaces;
    $[13] = t;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] !== t6) {
    t7 = <div className={styles.buttonContent}>{t5}<span>{t6}</span></div>;
    $[15] = t6;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  let t8;
  if ($[17] !== buttonVariant || $[18] !== handlePress || $[19] !== t4 || $[20] !== t7) {
    t8 = <Button onPress={handlePress} variant={buttonVariant} size="extraSmall" isPressed={t4} data-ua-click="playground-add-destinations">{t7}</Button>;
    $[17] = buttonVariant;
    $[18] = handlePress;
    $[19] = t4;
    $[20] = t7;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  return t8;
}